import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AppBaseHttpService } from '../../shared/services/app-base-http.service';
import { ATSDocument, WaiverDocument } from '../models';
import { Applicant, ApplicantDocument } from '../models/Applicant';
import { ApplicantApplicationWaiverDocument } from '../models/Applicant/applicant-application-waiver-document.model';
import { Application, ApplicationDocument, ApplicationPolygraph, ApplicationPsychological } from '../models/Application';
import { AppConfig } from './../../core/config/app.config';
import { AppConfigConstants } from './../../core/config/app.config.constants';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {

  constructor(private baseHttpService: AppBaseHttpService) { }

  getApplicationReport(application: Application): any {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
    AppConfigConstants.REPORT_URL_SEGMENT]).concat(`/application-report/${application.id}`);
    return this.baseHttpService.downloadDocumentGet(url);
  }

  getApplicantSupplementalReport(applicant: Applicant): any {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
    AppConfigConstants.REPORT_URL_SEGMENT]).concat(`/applicant-supplemental-report/${applicant.id}`);
    return this.baseHttpService.downloadDocumentGet(url);
  }

  getApplicationApplicantSupplementalDocumentLive(application: Application): any {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
    AppConfigConstants.REPORT_URL_SEGMENT]).concat(`/applicant-supplemental-report/${application.applicant.id}/${application.id}`);
    return this.baseHttpService.downloadDocumentGet(url);
  }

  getApplicationApplicantSupplementalDocument(application: Application): any {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
      AppConfigConstants.APPLICATION_URL_SEGMENT]).concat(`/get-document/${application?.applicant?.id}/${application?.id}`);
    return this.baseHttpService.downloadDocumentGet(url);
  }

  getActiveApplicationsByPolygrapherReport(): any {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
    AppConfigConstants.REPORT_URL_SEGMENT]).concat(`/active-applications-by-polygrapher-report`);
    return this.baseHttpService.downloadDocumentGet(url);
  }

  getApplicationsByRaceAndGenderReport(data: { submittedDateStart: Date, submittedDateEnd: Date }): any {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
    AppConfigConstants.REPORT_URL_SEGMENT]).concat(`/applications-by-race-and-gender-report`);
    return this.baseHttpService.downloadDocumentPost(url, data);
  }

  getApplicationsCertirfiedLEOPriorMilitaryReport(data: { submittedDateStart: Date, submittedDateEnd: Date }): any {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
    AppConfigConstants.REPORT_URL_SEGMENT]).concat(`/applications-certified-leo-prior-military-report`);
    return this.baseHttpService.downloadDocumentPost(url, data);
  }

  getApplicationsEyeExamBGIReport(data: { eyeExamStartDate: Date, eyeExamEndDate: Date }): any {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
    AppConfigConstants.REPORT_URL_SEGMENT]).concat(`/applications-eye-exam-bgi-report`);
    return this.baseHttpService.downloadDocumentPost(url, data);
  }

  getApplicationsPhysicalExamBGIReport(data: { physicalExamStartDate: Date, physicalExamEndDate: Date }): any {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
    AppConfigConstants.REPORT_URL_SEGMENT]).concat(`/applications-physical-exam-bgi-report`);
    return this.baseHttpService.downloadDocumentPost(url, data);
  }

  getApplicationsPolygraphTestReport(applicationPolygraph: ApplicationPolygraph): any {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
      AppConfigConstants.REPORT_URL_SEGMENT]).concat(`/applications-polygraph-test-report`);
    return this.baseHttpService.downloadDocumentPost(url, applicationPolygraph);
  }

  getApplicationsPsychologicalTestReport(applicationPsychological: ApplicationPsychological): any {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
    AppConfigConstants.REPORT_URL_SEGMENT]).concat(`/applications-psychological-test-report`);
    return this.baseHttpService.downloadDocumentPost(url, applicationPsychological);
  }

  getApplicantCountByCreatedDateReport(applicant: Applicant): any {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
      AppConfigConstants.REPORT_URL_SEGMENT]).concat(`/applicant-count-by-created-date-report`);
    return this.baseHttpService.downloadDocumentPost(url, applicant);
  }

  getApplicationsPATByRaceAndGenderReport(data: { eventStartDate: Date, eventEndDate: Date }): any {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
    AppConfigConstants.REPORT_URL_SEGMENT]).concat(`/applications-pat-by-race-and-gender-report`);
    return this.baseHttpService.downloadDocumentPost(url, data);
  }

  getApplicantDocument(applicantDocument: ApplicantDocument): Observable<Blob> {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
    AppConfigConstants.APPLICANT_URL_SEGMENT]).concat(`/download-document`);

    return this.baseHttpService.downloadDocumentPost(url, applicantDocument);
  }

  getApplicationDocument(applicationDocument: ApplicationDocument): Observable<Blob> {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
      AppConfigConstants.APPLICATION_URL_SEGMENT]).concat(`/download-application-document`);

    return this.baseHttpService.downloadDocumentPost(url, applicationDocument);
  }

  getATSDocument(document: ATSDocument): Observable<Blob> {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
    AppConfigConstants.ADMINISTRATION_URL_SEGMENT]).concat(`/downlaod-ats-document`);

    return this.baseHttpService.downloadDocumentPost(url, document);
  }

  getATSWaiverDocument(document: ATSDocument): Observable<Blob> {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
    AppConfigConstants.ADMINISTRATION_URL_SEGMENT]).concat(`/downlaod-ats-waiver-document`);

    return this.baseHttpService.downloadDocumentPost(url, document);
  }

  getApplicantApplicationWaiverDocument(applicantApplicationWaiverDocument: ApplicantApplicationWaiverDocument) {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
      AppConfigConstants.APPLICANT_URL_SEGMENT]).concat(`/download-applicant-application-waiver-document-file`);
                                                          
    return this.baseHttpService.downloadDocumentPost(url, applicantApplicationWaiverDocument);
  }

  getApplicationBGIReport(application: Application): any {
    const url = AppConfig.appSettingConcat([AppConfigConstants.API_SERVER_URL,
      AppConfigConstants.REPORT_URL_SEGMENT]).concat(`/application-bgi-report/${application.id}`);
    return this.baseHttpService.downloadDocumentGet(url);
  }

}
